import { ContentType } from '@amzn/gbsa-helios-typescript-client';
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { mediaService } from 'src/data/api/helios-service/mediaService';
import { ApiRequestStatus, initApiStatus } from 'src/types/ApiRequestStatus';
import {
  DeleteMediaRequest,
  GetMediaRequest,
  ListMediaByIdsRequest,
  ListMediaRequest,
  MediaData,
  MediaDetails,
  UploadMediaRequest,
} from 'src/types/helios/Media';

import { handleError } from 'src/utils/generalUtils';

export interface UploadMediaApiStatus extends ApiRequestStatus {
  mediaData?: MediaDetails;
  image?: string;
  progress?: {
    completed: number;
    total: number;
  };
}

export interface DeleteMediaApiStatus extends ApiRequestStatus {
  mediaId?: string;
}
export interface MediaState {
  mediaList: MediaData[];
  currentMedia?: MediaData;
  mediaIdsMap: Record<string, string[]>;
  uploadMediaApiStatus: UploadMediaApiStatus;
  getMediaApiStatus: ApiRequestStatus;
  listMediaApiStatus: ApiRequestStatus;
  listMediaByIdsApiStatus: ApiRequestStatus;
  deleteMediaApiStatus: DeleteMediaApiStatus;
}

const initialState: MediaState = {
  mediaList: [],
  currentMedia: undefined,
  mediaIdsMap: {},
  uploadMediaApiStatus: initApiStatus,
  getMediaApiStatus: initApiStatus,
  listMediaApiStatus: initApiStatus,
  deleteMediaApiStatus: initApiStatus,
  listMediaByIdsApiStatus: initApiStatus,
};

export const uploadMediaApiThunk = createAsyncThunk(
  'media/uploadMedia',
  async (data: UploadMediaRequest, { rejectWithValue }) => {
    try {
      const response = await mediaService.upload({
        file: data.file,
        businessUnit: data.businessUnit,
        entityType: data.entityType,
        createdBy: data.createdBy,
        contentType: data.contentType as ContentType,
        programId: data.programId,
        fileName: data.fileName,
      });
      return {
        media: response.media,
      };
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

export const uploadMultipleMediaApiThunk = createAsyncThunk(
  'media/uploadMultipleMedia',
  async (dataList: UploadMediaRequest[], { dispatch, getState }) => {
    const results = [];
    const total = dataList.length;

    // Process uploads sequentially
    for (let i = 0; i < dataList.length; i++) {
      const data = dataList[i];

      // Update progress before starting each upload
      dispatch(updateUploadProgress({ completed: i, total }));

      try {
        const resultAction = await dispatch(uploadMediaApiThunk(data));
        if (uploadMediaApiThunk.fulfilled.match(resultAction)) {
          results.push(resultAction.payload);
        }
      } catch (error) {
        // Continue with next upload even if one fails
        console.error('Failed to upload media:', error);
      }
    }

    // Update progress to completion
    dispatch(updateUploadProgress({ completed: total, total }));

    return results;
  },
);

export const getMediaApiThunk = createAsyncThunk(
  'media/getMedia',
  async (data: GetMediaRequest, { rejectWithValue }) => {
    try {
      const response = await mediaService.getById(data);
      return response;
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

export const listMediaApiThunk = createAsyncThunk(
  'media/listMedia',
  async (data: ListMediaRequest, { rejectWithValue }) => {
    try {
      const response = await mediaService.list(data);
      return response;
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

export const listMediaByIdsApiThunk = createAsyncThunk(
  'media/listMediaByIds',
  async (data: ListMediaByIdsRequest, { rejectWithValue }) => {
    try {
      const response = await mediaService.listByIds(data);
      return response;
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

export const deleteMediaApiThunk = createAsyncThunk(
  'media/deleteMedia',
  async (data: DeleteMediaRequest, { rejectWithValue }) => {
    try {
      await mediaService.delete(data);
      return {
        mediaId: data.mediaId,
      };
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

const mediaSlice = createSlice({
  name: 'media',
  initialState,
  reducers: {
    clearAllApiStatus: (state) => {
      state.uploadMediaApiStatus = initApiStatus;
      state.getMediaApiStatus = initApiStatus;
      state.listMediaApiStatus = initApiStatus;
      state.deleteMediaApiStatus = initApiStatus;
      state.listMediaByIdsApiStatus = initApiStatus;
    },
    updateUploadProgress: (
      state,
      action: PayloadAction<{ completed: number; total: number }>,
    ) => {
      state.uploadMediaApiStatus.progress = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Upload media cases
      .addCase(uploadMediaApiThunk.pending, (state) => {
        state.uploadMediaApiStatus.isLoading = true;
        state.uploadMediaApiStatus.errorMessage = null;
        state.uploadMediaApiStatus.successMessage = null;
      })
      .addCase(uploadMediaApiThunk.fulfilled, (state, action) => {
        state.uploadMediaApiStatus.isLoading = false;
        state.uploadMediaApiStatus.isSuccess = true;
        state.uploadMediaApiStatus.mediaData = action.payload.media;
        // Store the uploaded media ID in the mediaIdsMap
        const mediaId = action.payload.media.mediaId;
        const fieldType = action.meta.arg.fieldType || 'default';

        if (!state.mediaIdsMap[fieldType]) {
          state.mediaIdsMap[fieldType] = [];
        }
        state.mediaIdsMap[fieldType].push(mediaId);
      })
      .addCase(uploadMediaApiThunk.rejected, (state, action) => {
        state.uploadMediaApiStatus.isLoading = false;
        state.uploadMediaApiStatus.isSuccess = false;
        state.uploadMediaApiStatus.errorMessage = action.payload as string;
        state.uploadMediaApiStatus.successMessage = null;
      })

      // Get media cases
      .addCase(getMediaApiThunk.pending, (state) => {
        state.getMediaApiStatus.isLoading = true;
        state.getMediaApiStatus.errorMessage = null;
      })
      .addCase(getMediaApiThunk.fulfilled, (state, action) => {
        state.getMediaApiStatus.isLoading = false;
        state.currentMedia = action.payload.mediaResponse;
      })
      .addCase(getMediaApiThunk.rejected, (state, action) => {
        state.getMediaApiStatus.isLoading = false;
        state.getMediaApiStatus.errorMessage = action.payload as string;
      })

      // List media cases
      .addCase(listMediaApiThunk.pending, (state) => {
        state.listMediaApiStatus.isLoading = true;
        state.listMediaApiStatus.errorMessage = null;
        state.mediaList = [];
      })
      .addCase(listMediaApiThunk.fulfilled, (state, action) => {
        state.listMediaApiStatus.isLoading = false;
        state.mediaList = action.payload.items;
        state.listMediaApiStatus.nextToken = action.payload.nextToken;
      })
      .addCase(listMediaApiThunk.rejected, (state, action) => {
        state.listMediaApiStatus.isLoading = false;
        state.listMediaApiStatus.errorMessage = action.payload as string;
      })
      //List medias by Ids
      .addCase(listMediaByIdsApiThunk.pending, (state) => {
        state.listMediaByIdsApiStatus.isLoading = true;
        state.listMediaByIdsApiStatus.errorMessage = null;
        state.mediaList = [];
      })
      .addCase(listMediaByIdsApiThunk.fulfilled, (state, action) => {
        state.listMediaByIdsApiStatus.isLoading = false;
        state.mediaList = action.payload.items;
        state.listMediaByIdsApiStatus.nextToken = action.payload.nextToken;
      })
      .addCase(listMediaByIdsApiThunk.rejected, (state, action) => {
        state.listMediaByIdsApiStatus.isLoading = false;
        state.listMediaByIdsApiStatus.errorMessage = action.payload as string;
      })

      // Delete media cases
      .addCase(deleteMediaApiThunk.pending, (state) => {
        state.deleteMediaApiStatus.isLoading = true;
        state.deleteMediaApiStatus.errorMessage = null;
        state.deleteMediaApiStatus.successMessage = null;
      })
      .addCase(deleteMediaApiThunk.fulfilled, (state, action) => {
        state.deleteMediaApiStatus.isLoading = false;
        state.deleteMediaApiStatus.isSuccess = true;
        state.deleteMediaApiStatus.mediaId = action.payload.mediaId;
        // Remove the deleted media from the list
        state.mediaList = state.mediaList.filter(
          (item) => item.media.mediaId !== action.payload.mediaId,
        );
      })
      .addCase(deleteMediaApiThunk.rejected, (state, action) => {
        state.deleteMediaApiStatus.isLoading = false;
        state.deleteMediaApiStatus.isSuccess = false;
        state.deleteMediaApiStatus.errorMessage = action.payload as string;
        state.deleteMediaApiStatus.successMessage = null;
      });
  },
});

export const { clearAllApiStatus, updateUploadProgress } = mediaSlice.actions;
export default mediaSlice.reducer;
